import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, take } from 'rxjs/operators';

import { AlertService } from '@services/alert.service';
import { CompaniesService } from '@services/companies.service';
import { CompaniesRepository } from '@data-layer/repositories';
import { CompanyModel } from '@data-layer/models/companies';

@Injectable({ providedIn: 'root' })
export class LhcbMigrationPopupService {
  public readonly activeCompanyForPopup$: BehaviorSubject<CompanyModel | null> = new BehaviorSubject<CompanyModel | null>(null);
  public readonly isAcknowledging$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly migrationPopupActiveOrPending$: Observable<boolean>;

  private isInitialized: boolean = false;
  private readonly acknowledgedCompanyIds$: BehaviorSubject<Set<number>> = new BehaviorSubject<Set<number>>(new Set<number>());

  constructor(
    private companiesService: CompaniesService,
    private companiesRepository: CompaniesRepository,
    private alertService: AlertService,
    private router: Router,
  ) {
    this.migrationPopupActiveOrPending$ = combineLatest([
      this.companiesService.companyChange$,
      this.activeCompanyForPopup$,
      this.isAcknowledging$,
      this.acknowledgedCompanyIds$,
    ]).pipe(
      map(
        ([change, active, isAcknowledging, acknowledged]: [
          { previous: CompanyModel; current: CompanyModel },
          CompanyModel | null,
          boolean,
          Set<number>,
        ]) => {
          if (active != null) {
            return true;
          }
          if (isAcknowledging) {
            return true;
          }
          const current: CompanyModel | null = change?.current;
          if (current?.id == null) {
            return false;
          }
          if (current.migrationPopupShouldShow !== true) {
            return false;
          }
          return !acknowledged.has(current.id);
        },
      ),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  public init(): void {
    if (this.isInitialized) {
      return;
    }
    this.isInitialized = true;

    // Intentionally not torn down: this service is root-scoped and must
    // react to company changes for the entire app lifetime.
    this.companiesService.companyChange$.subscribe((change: { previous: CompanyModel; current: CompanyModel }) => {
      this.evaluate(change?.current);
    });
  }

  public acknowledgeAndClose(): void {
    this.acknowledge(() => this.activeCompanyForPopup$.next(null));
  }

  public acknowledgeAndNavigate(): void {
    this.acknowledge((companyId: number) => {
      this.activeCompanyForPopup$.next(null);
      this.router.navigate(['/companies', 'account-settings', 'company-view', companyId], {
        queryParams: { isEditViewOpen: true },
        fragment: 'power-of-attorney',
      });
    });
  }

  private evaluate(company: CompanyModel | null | undefined): void {
    if (company == null || company.id == null) {
      return;
    }
    if (company.migrationPopupShouldShow !== true) {
      return;
    }
    if (this.acknowledgedCompanyIds$.value.has(company.id)) {
      return;
    }
    if (this.isAcknowledging$.value) {
      return;
    }
    if (this.activeCompanyForPopup$.value?.id === company.id) {
      return;
    }
    this.activeCompanyForPopup$.next(company);
  }

  private acknowledge(onSuccess: (companyId: number) => void): void {
    const company: CompanyModel | null = this.activeCompanyForPopup$.value;
    if (company == null || company.id == null) {
      return;
    }
    if (this.isAcknowledging$.value) {
      return;
    }
    const companyId: number = company.id;
    this.isAcknowledging$.next(true);

    this.companiesRepository
      .acknowledgeMigrationPopup(companyId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          const nextAcknowledged: Set<number> = new Set<number>(this.acknowledgedCompanyIds$.value);
          nextAcknowledged.add(companyId);
          this.acknowledgedCompanyIds$.next(nextAcknowledged);
          this.isAcknowledging$.next(false);
          onSuccess(companyId);
        },
        error: () => {
          this.isAcknowledging$.next(false);
          this.alertService.error('LHCB_MIGRATION_POPUP.ACKNOWLEDGE_FAILED');
        },
      });
  }
}
